import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './Cart.css'; // Your CSS file for styling
import UsersTopbar from '../UsersTopbar/UsersTopbar';
import api from '../../api';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import useBlockCheck from '../../hooks/useBlockCheck'; // Import the hook
import { FaStar, FaPlay } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { getDisplayPrices } from '../../utils/priceUtils';

const Cart = () => {
  useBlockCheck('user'); // 🔥 Prevents blocked users from accessing this page
  const [searchQuery, setSearchQuery] = useState(''); // Add search query state
  const [selectedCourses, setSelectedCourses] = useState([]); // Track selected courses
  const { token } = useAuth();
  const { cartItems, removeFromCart, fetchCartItems } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    // Cart items are now managed by CartContext
    // Only fetch if we don't have cart items yet
    if (cartItems.length === 0) {
      fetchCartItems();
    }
  }, [fetchCartItems, cartItems.length]);

  const handleViewCourse = (courseId) => {
    navigate(`/courses/${courseId}`);
  };

  const handleRemoveFromCart = async (courseId) => {
    try {
      await removeFromCart(courseId);
      toast.success('Course removed from cart');
    } catch (error) {
      console.error('Error removing from cart:', error);
      toast.error('Failed to remove course from cart');
    }
  };

  const handleCheckboxChange = (courseId) => {
    setSelectedCourses((prev) =>
      prev.includes(courseId)
        ? prev.filter((id) => id !== courseId)
        : [...prev, courseId],
    );
  };

  const handleProceedToCheckout = async () => {
    if (selectedCourses.length === 0) {
      toast.warning('Please select at least one course to proceed.');
      return;
    }

    try {
      const freeCourses = cartItems.filter(
        (item) => {
          const prices = getDisplayPrices(item);
          return selectedCourses.includes(item._id) && prices.offerPrice === 0;
        },
      );

      if (freeCourses.length > 0) {
        await Promise.all(
          freeCourses.map((course) =>
            axios.post(
              api.enrollFreeCourse,
              { courseId: course._id },
              { headers: { Authorization: `Bearer ${token}` } },
            ),
          ),
        );

        // Remove free courses from the cart
        setSelectedCourses((prev) =>
          prev.filter((id) => !freeCourses.some((freeCourse) => freeCourse._id === id)),
        );
        
        // Refresh cart items to reflect the changes
        await fetchCartItems();

        toast.success('You have successfully enrolled in free courses.');
      }

      // Proceed to checkout for paid courses
      const paidCourses = selectedCourses.filter(
        (id) => !freeCourses.some((freeCourse) => freeCourse._id === id),
      );

      if (paidCourses.length > 0) {
        navigate(`/checkout?courseIds=${paidCourses.join(',')}`);
      }
    } catch (error) {
      console.error('Error during checkout:', error);
      toast.error('Failed to process checkout. Please try again.');
    }
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      // The search is already live/filtered, so we just need to prevent form submission
    }
  };

  // Filter items based on search query
  const filteredCart = cartItems.filter((item) =>
    item.title.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  return (
    <div>
      <UsersTopbar />

      <div className="cart-container">
        <h1 className="cart-heading">My Cart</h1>

        {/* Search Bar */}
        <input
          type="text"
          placeholder="Search in cart..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyPress={handleSearchKeyPress}
          className="cart-search-bar"
        />

        <div className="cart-items">
          {filteredCart.length === 0 ? (
            <div className="cart-empty-wrapper">
              <p className="cart-p">
                {searchQuery
                  ? 'No courses match your search.'
                  : 'Your cart is empty. Start exploring courses!'}
              </p>
              <button
                className="cart-browse-button"
                onClick={() => navigate('/category/all-courses/all-courses?search=')}
              >
                Browse Courses
              </button>

            </div>
          ) : (
            filteredCart.map((course) => (
              <div className="cart-course-card" key={course._id}>
                <div className="cart-course-card-image-wrapper" onClick={() => navigate(`/courses/${course._id}`)}>
                  {course.image && (
                    <img
                      src={`${api.img + course.image}`}
                      alt={course.title}
                      className="cart-course-card-image"
                    />
                  )}
                  <label
                    className="cart-checkbox-wrapper"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input
                      type="checkbox"
                      checked={selectedCourses.includes(course._id)}
                      onChange={() => handleCheckboxChange(course._id)}
                    />
                  </label>

                  <div className="cart-play-button-overlay">
                    <FaPlay className="play-icon" />
                  </div>

                </div>
                <div>
                  <div className="cart-course-price-box">
                    {(() => {
                      const prices = getDisplayPrices(course);
                      return (
                        <>
                          {/* Offer Price Section */}
                          <span className="cart-course-offer-price">
                            {prices.offerPrice === 0 ? (
                              <span className="cart-price-free">Free</span>
                            ) : (
                              `₹ ${prices.offerPrice}`
                            )}
                          </span>
                          <span className="cart-price-original">
                            ₹{prices.actualPrice}
                          </span>

                          {prices.offerPrice !== -1 && (
                            <span
                              className="cart-price-discount"
                              style={{ marginLeft: '8px' }}
                            >
                              {Math.round(
                                ((prices.actualPrice - prices.offerPrice) /
                                  prices.actualPrice) *
                                100,
                              )}
                              % Off
                            </span>
                          )}
                        </>
                      );
                    })()}
                  </div>

                  <div className="cart-course-card-box">
                    <h3 className="cart-course-card-h3">{course.title}</h3>
                    {/* <p className="cart-course-card-p">{course.description}</p> */}
                  </div>
                  {/* <div className="course-instructor">
                                  <p>Instructor: {course.instructor}</p>
                                </div> */}
                  {/* <div className="course-updated">
                                  🗓 Last Updated: {course.lastUpdated ? format(new Date(course.lastUpdated), "MMMM yyyy") : "N/A"}
                                </div> */}
                  <div className="course-rating">
                    {Array.from({ length: 5 }).map((_, index) => (
                      <FaStar
                        key={index}
                        className={
                          index < Math.round(course.averageRating)
                            ? 'star filled'
                            : 'star'
                        }
                      // onClick={() =>
                      //   handleRatingChange(course._id, index + 1)
                      // }
                      />
                    ))}
                  </div>
                  <div className="cart-button-container">
                    <button
                      className="cart-remove-button"
                      onClick={() => handleRemoveFromCart(course._id)}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {filteredCart.length > 0 && (
        <button
          className={`cart-checkout-button ${
            selectedCourses.length === 0 ? 'cart-checkout-button--inactive' : ''
          }`}
          onClick={handleProceedToCheckout}
        >
          Proceed to Checkout
        </button>
      )}
    </div>
  );
};

export default Cart;
